import { DeviceEnrollmentScopeType, DeviceEnrollmentTokenStatus, LocationTypeEnum } from "../generated/graphql";

export const MAX_TOKEN_VALIDITY_DAYS = 90;

const MASK_CHARACTER = "•";
const MASKED_GROUP = MASK_CHARACTER.repeat(4);
const UNLIMITED_USES_LABEL = "∞";

export const maskEnrollmentCode = (code: string | null | undefined): string => {
  if (!code) return `${MASKED_GROUP}-${MASKED_GROUP}`;

  const separatorIndex = code.indexOf("-");
  if (separatorIndex < 0) return MASKED_GROUP;

  return `${code.slice(0, separatorIndex)}-${MASKED_GROUP}`;
};

export type TokenUsesInput = {
  useCount: number;
  maxUses?: number | null;
};

export const formatTokenUses = ({ useCount, maxUses }: TokenUsesInput): string => {
  const budget = maxUses?.toString() ?? UNLIMITED_USES_LABEL;
  return `${useCount} / ${budget}`;
};

export type RevealableToken = {
  status: DeviceEnrollmentTokenStatus;
  implicit: boolean;
};

export const canRevealToken = ({ status, implicit }: RevealableToken): boolean =>
  status === DeviceEnrollmentTokenStatus.Active && !implicit;

// Translation keys; render through translate().
export const SCOPE_TYPE_LABELS: Record<DeviceEnrollmentScopeType, string> = {
  [DeviceEnrollmentScopeType.Organization]: "deviceEnrollment.scope.ORGANIZATION",
  [DeviceEnrollmentScopeType.Site]: "deviceEnrollment.scope.SITE",
  [DeviceEnrollmentScopeType.Department]: "deviceEnrollment.scope.DEPARTMENT",
  [DeviceEnrollmentScopeType.Ward]: "deviceEnrollment.scope.WARD",
};

export const SCOPE_TYPE_HINTS: Record<DeviceEnrollmentScopeType, string> = {
  [DeviceEnrollmentScopeType.Organization]: "deviceEnrollment.scope.hint.ORGANIZATION",
  [DeviceEnrollmentScopeType.Site]: "deviceEnrollment.scope.hint.SITE",
  [DeviceEnrollmentScopeType.Department]: "deviceEnrollment.scope.hint.DEPARTMENT",
  [DeviceEnrollmentScopeType.Ward]: "deviceEnrollment.scope.hint.WARD",
};

export const SCOPE_TYPE_ORDER: readonly DeviceEnrollmentScopeType[] = [
  DeviceEnrollmentScopeType.Organization,
  DeviceEnrollmentScopeType.Site,
  DeviceEnrollmentScopeType.Department,
  DeviceEnrollmentScopeType.Ward,
];

export const getTokenStatusLabelKey = (status: DeviceEnrollmentTokenStatus): string =>
  `deviceEnrollment.status.${status.toUpperCase()}`;

export const SCOPE_TYPE_LOCATION_TYPES: Record<DeviceEnrollmentScopeType, LocationTypeEnum | null> = {
  [DeviceEnrollmentScopeType.Organization]: null,
  [DeviceEnrollmentScopeType.Site]: LocationTypeEnum.Site,
  [DeviceEnrollmentScopeType.Department]: LocationTypeEnum.Department,
  [DeviceEnrollmentScopeType.Ward]: LocationTypeEnum.Ward,
};

export const getScopeTypeLabel = (scopeType: DeviceEnrollmentScopeType): string => SCOPE_TYPE_LABELS[scopeType];

export const getScopeLocationType = (scopeType: DeviceEnrollmentScopeType): LocationTypeEnum | null =>
  SCOPE_TYPE_LOCATION_TYPES[scopeType];

export const scopeTypeRequiresLocation = (scopeType: DeviceEnrollmentScopeType): boolean =>
  SCOPE_TYPE_LOCATION_TYPES[scopeType] !== null;

export type DurationOption = {
  hours: number;
  labelKey: string;
};

export const DURATION_OPTIONS: readonly DurationOption[] = [
  { hours: 1, labelKey: "deviceEnrollment.duration.h1" },
  { hours: 4, labelKey: "deviceEnrollment.duration.h4" },
  { hours: 24, labelKey: "deviceEnrollment.duration.h24" },
  { hours: 48, labelKey: "deviceEnrollment.duration.h48" },
  { hours: 24 * 7, labelKey: "deviceEnrollment.duration.d7" },
  { hours: 24 * 30, labelKey: "deviceEnrollment.duration.d30" },
];

export const DEFAULT_DURATION_HOURS = 48;

export type CreateTokenFormState = {
  organizationId: string;
  scopeType: DeviceEnrollmentScopeType;
  locationId?: string | null;
  durationHours: number;
  maxUses?: number | null;
};

export type CreateTokenInput = {
  organizationId: string;
  locationId?: string;
  expiresAt: string;
  maxUses: number | null;
};

const MILLISECONDS_PER_HOUR = 60 * 60 * 1000;

export const buildCreateTokenInput = (form: CreateTokenFormState, now: Date = new Date()): CreateTokenInput => {
  const expiresAt = new Date(now.getTime() + form.durationHours * MILLISECONDS_PER_HOUR).toISOString();
  const locationId = scopeTypeRequiresLocation(form.scopeType) ? form.locationId : undefined;

  return {
    organizationId: form.organizationId,
    ...(locationId ? { locationId } : {}),
    expiresAt,
    maxUses: form.maxUses ?? null,
  };
};

export enum CreateTokenFormError {
  LocationRequired = "LOCATION_REQUIRED",
  DurationTooLong = "DURATION_TOO_LONG",
  DurationNotPositive = "DURATION_NOT_POSITIVE",
  MaxUsesNotPositiveInteger = "MAX_USES_NOT_POSITIVE_INTEGER",
}

export type CreateTokenFormValidation = {
  valid: boolean;
  errors: CreateTokenFormError[];
};

const MAX_TOKEN_VALIDITY_HOURS = MAX_TOKEN_VALIDITY_DAYS * 24;

export const validateCreateTokenForm = (form: CreateTokenFormState): CreateTokenFormValidation => {
  const errors: CreateTokenFormError[] = [];

  if (scopeTypeRequiresLocation(form.scopeType) && !form.locationId) {
    errors.push(CreateTokenFormError.LocationRequired);
  }

  if (form.durationHours <= 0) {
    errors.push(CreateTokenFormError.DurationNotPositive);
  } else if (form.durationHours > MAX_TOKEN_VALIDITY_HOURS) {
    errors.push(CreateTokenFormError.DurationTooLong);
  }

  if (form.maxUses != null && (!Number.isInteger(form.maxUses) || form.maxUses < 1)) {
    errors.push(CreateTokenFormError.MaxUsesNotPositiveInteger);
  }

  return { valid: errors.length === 0, errors };
};
